import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import {
  Badge,
  EmptyIndicator,
  LoadingIndicator,
  TitleWithIcon,
  User
} from 'components/common';
import { Box, BoxBackLink, BoxRow } from 'components/common/box';
import { ParticipantsStats } from 'components/common/stats';
import { PathProgressStatus } from 'components/paths';
import {
  MyTeamFollowBox,
  useMyTeamFollowContext,
  useTeamUserList
} from '.';

import type { GetTeamPathsRes } from 'shared-types/responses';

const MyTeamPathsPath = () => {
  const { messages, user } = useReduxState();

  const { queryParams } = useMyTeamFollowContext();

  const pathId = queryParams['pathId'] as string;

  // Fetch only on the path id so changing the user-list filters
  // (page/search/participantsOnly) never refetches — the single-path
  // response already carries every supervised user, so filtering is local.
  const { data, loading } = useApiData<GetTeamPathsRes>({
    url: `/admin/supervisors/${user.id}/team-paths?pathId=${pathId}`,
    onlyIf: Boolean(user?.id && pathId)
  });

  const path = data?.paths?.[0];

  const { numberOfPages, participantsOnly, users } = useTeamUserList({
    isParticipant: (user) =>
      Boolean(path?.progressData?.progressDataByUser?.[user.id]),
    supervisedUsers: data?.supervisedUsers || []
  });

  return loading && !path ? (
    <LoadingIndicator />
  ) : (
    <>
      <Box
        title={
          <BoxBackLink
            as="/my-team/follow/paths"
            href="/my-team/follow/paths"
            text={messages.pathsAllPaths}
          />
        }
      >
        {path ? (
          <div className="row-sm row-center">
            <div className="col">
              <TitleWithIcon
                icon="tasks"
                title={path.title}
                titleLink={{
                  as: `/paths/${path.id}`,
                  href: '/paths/[pathId]'
                }}
              >
                {getMessageWithPlaceholders({
                  message: messages.pathsNumberOfSteps,
                  placeholders: { numberOfSteps: path.steps?.length }
                })}
              </TitleWithIcon>
            </div>
            <div className="col-min">
              <ParticipantsStats
                completionEnabled={true}
                progressData={{
                  ...path.progressData,
                  // participantsOnly narrows the bar max to supervised users
                  // added to the path; completed/started are unchanged.
                  participants: participantsOnly
                    ? path.addedUserIds?.length || 0
                    : path.progressData.participants
                }}
                notStartedLabel={
                  participantsOnly
                    ? messages.tasksUsersNotStartedSingular
                    : messages.commonNotStartedOrNotAdded
                }
              />
            </div>
          </div>
        ) : null}
      </Box>

      <MyTeamFollowBox
        numberOfPages={numberOfPages}
        style={{ marginTop: 20 }}
        title={messages.commonUsers}
      >
        {loading ? (
          <LoadingIndicator />
        ) : users?.length ? (
          users.map((user) => (
            <BoxRow key={user.id}>
              <div className="row-sm row-center">
                <div className="col">
                  <User enableProfileLink={true} user={user} />
                </div>
                <div className="col-min">
                  {path?.progressData?.progressDataByUser?.[user.id] &&
                  path.steps ? (
                    <PathProgressStatus
                      id={`my-team-paths-progress-status-${path.id}-${user.id}`}
                      notStartedLabel={
                        path?.addedUserIds?.includes(user.id)
                          ? messages.commonNotStarted
                          : messages.commonNotAdded
                      }
                      progressData={
                        path.progressData.progressDataByUser[user.id]
                      }
                      steps={path.steps}
                    />
                  ) : (
                    <Badge isRounded={false} text={messages.commonNotAdded} />
                  )}
                </div>
              </div>
            </BoxRow>
          ))
        ) : (
          <EmptyIndicator icon="user" title={messages.commonNoUsers} />
        )}
      </MyTeamFollowBox>
    </>
  );
};

export default MyTeamPathsPath;
